import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, ShieldCheck, Info, RefreshCw, Check, Undo2, AlertTriangle, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const REFRESH_INTERVAL_MS = 60_000;   // Live-poll every 60s
const OVER_MAP_THRESHOLD  = 20;       // Flag subject-router at >20 degrees

/**
 * OfferRateCoverageCard — admin dashboard tile.
 * Sections: per-country coverage %, 7-day sparkline, subject-router
 * counts, and a "kick all daemons" button for when a fresh Serper
 * key needs draining fast.
 */
export default function OfferRateCoverageCard() {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState(null);
  const [recent, setRecent] = useState(null);
  const [err, setErr] = useState(null);
  const [kicking, setKicking] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r1, r2, r3] = await Promise.all([
        api.get("/admin/coverage/offer-rate"),
        api.get("/admin/coverage/offer-rate/history").catch(() => ({ data: { by_country: {} } })),
        api.get("/admin/coverage/recent-extractions?limit=10").catch(() => ({ data: { items: [] } })),
      ]);
      setData(r1.data);
      setHistory(r2.data?.by_country || {});
      setRecent(r3.data?.items || []);
      setLastRefreshedAt(new Date());
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load coverage");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Live polling — pipeline writes continuously, so the card should
  // reflect it without a page reload.
  useEffect(() => {
    const t = setInterval(load, REFRESH_INTERVAL_MS);
    return () => clearInterval(t);
  }, [load]);

  const nudge = async () => {
    setKicking(true);
    try {
      const r = await api.post("/admin/coverage/nudge-daemons");
      const kicked = r.data.kicked ?? r.data.count;
      const msg = r.data.message
        ? r.data.message
        : `Kicked ${kicked} daemons`;
      toast.success(msg, { duration: 6000 });
      setTimeout(load, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nudge failed");
    } finally {
      setKicking(false);
    }
  };

  const undoLast = async () => {
    if (!window.confirm(
      "Restore acceptance_rate for every uni nulled by the most recent sunset run. "
      + "Only affects rows written within a 60-second window of the last sunset timestamp. "
      + "Proceed?"
    )) return;
    setUndoing(true);
    try {
      const r = await api.post("/admin/coverage/undo-sunset");
      if (r.data.undone === 0) {
        toast.info(r.data.message || "Nothing to undo");
      } else {
        toast.success(`Restored ${r.data.undone} acceptance_rate values`, { duration: 6000 });
      }
      setTimeout(load, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Undo failed");
    } finally {
      setUndoing(false);
    }
  };

  const hasSunsetHistory = data?.by_country?.some((c) => c.sunset_done > 0);

  return (
    <div
      data-testid="admin-offer-rate-coverage-card"
      className="p-6 border border-stone-300 rounded-xl bg-white"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <ShieldCheck className="h-6 w-6 text-stone-800" />
          <h2 className="mt-3 font-editorial text-xl">Offer-rate coverage</h2>
          <p className="mt-2 text-sm text-stone-600 leading-relaxed max-w-2xl">
            Watch the sunset gate approach in real time. When a country's
            coverage crosses the threshold, the stub-null cron nulls every
            seed-default <code>acceptance_rate</code> on its next hourly
            pass.
          </p>
        </div>
        <div className="shrink-0 flex items-center gap-2">
          {hasSunsetHistory && (
            <button
              data-testid="coverage-undo-btn"
              onClick={undoLast}
              disabled={undoing}
              className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900 text-xs font-underground-sc tracking-wider px-3 py-1.5 transition disabled:opacity-50"
              title="Undo the most recent stub-null sunset run — restores acceptance_rate for every uni nulled in the last batch."
            >
              {undoing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Undo2 className="h-3 w-3" />}
              Undo last sunset
            </button>
          )}
          <button
            data-testid="coverage-nudge-btn"
            onClick={nudge}
            disabled={kicking}
            className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-white hover:bg-stone-50 text-xs font-underground-sc tracking-wider px-3 py-1.5 transition disabled:opacity-50"
            title="Restart every wua-* enrichment daemon so they immediately start a fresh pass."
          >
            {kicking ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            Kick daemons
          </button>
        </div>
      </div>

      {err && (
        <div className="mt-4 text-sm text-rose-700" data-testid="coverage-error">{err}</div>
      )}
      {!data && !err && (
        <div className="mt-4 text-sm text-stone-500 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}
      {data && (
        <div className="mt-5">
          <div className="text-[10px] font-underground-sc tracking-widest text-stone-500 mb-2">
            Threshold: {(data.threshold * 100).toFixed(0)}%
          </div>
          <div className="space-y-3">
            {data.by_country.map((c) => (
              <CoverageRow
                key={c.country}
                c={c}
                threshold={data.threshold}
                trend={(history || {})[c.country] || []}
              />
            ))}
          </div>

          <RecentExtractionsFeed items={recent} />
          <div className="mt-4 text-[10px] font-mono text-stone-400 flex items-center justify-between gap-1.5">
            <span className="inline-flex items-center gap-1.5">
              <Info className="h-3 w-3" />
              Active pool = universities either reachable by Serper (scholarships URL found) OR already carrying a verified offer_rate.
              Sparkline covers the last 7 days.
            </span>
            {lastRefreshedAt && (
              <span data-testid="coverage-last-refreshed" title={lastRefreshedAt.toISOString()}>
                Auto-refreshing · updated {lastRefreshedAt.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Sparkline({ points, height = 28, width = 96 }) {
  if (!points || points.length < 2) {
    return (
      <div
        data-testid="sparkline-empty"
        className="text-[10px] font-mono text-stone-400 italic"
        style={{ height, width }}
      >
        No 7-day data yet
      </div>
    );
  }
  const ys = points.map((p) => p.coverage_pct || 0);
  const min = Math.min(...ys);
  const max = Math.max(...ys, 0.001); // avoid div-by-zero
  const range = max - min || 1;
  const stepX = width / (points.length - 1);
  const path = points
    .map((p, i) => {
      const x = i * stepX;
      const y = height - ((p.coverage_pct - min) / range) * (height - 4) - 2;
      return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");
  const last = points[points.length - 1];
  const first = points[0];
  const delta = (last.coverage_pct || 0) - (first.coverage_pct || 0);
  const stroke = delta >= 0 ? "#059669" : "#dc2626";
  const arrow = delta > 0 ? "↑" : delta < 0 ? "↓" : "→";
  return (
    <div className="flex items-center gap-1.5" data-testid="sparkline">
      <svg width={width} height={height} className="overflow-visible">
        <path d={path} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        {/* Last point dot */}
        <circle
          cx={(points.length - 1) * stepX}
          cy={height - (((last.coverage_pct || 0) - min) / range) * (height - 4) - 2}
          r="2"
          fill={stroke}
        />
      </svg>
      <span
        className="text-[10px] font-mono"
        style={{ color: stroke }}
        title={`Δ over 7 days: ${(delta * 100).toFixed(2)} pts`}
      >
        {arrow} {Math.abs(delta * 100).toFixed(1)}pp
      </span>
    </div>
  );
}

function CoverageRow({ c, threshold, trend }) {
  const pct = (c.coverage_pct || 0) * 100;
  const above = c.coverage_pct >= threshold;
  const subjects = c.routed_by_subject || [];
  return (
    <div
      data-testid={`coverage-row-${c.country}`}
      className="border border-stone-200 rounded-lg p-3"
    >
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="font-mono text-sm font-semibold flex items-baseline gap-2">
          <span>{c.country}</span>
          <span className="text-stone-400">·</span>
          <span className={`font-mono text-xl ${above ? "text-emerald-700" : "text-stone-800"}`}>
            {pct.toFixed(1)}%
          </span>
          <Sparkline points={trend} />
        </div>
        <span
          className={`text-[10px] font-underground-sc tracking-wider rounded-full border px-2 py-0.5 ${
            above
              ? "border-emerald-200 bg-emerald-50 text-emerald-800"
              : "border-amber-200 bg-amber-50 text-amber-800"
          }`}
        >
          {above ? <><Check className="inline h-2.5 w-2.5 mr-0.5" /> Sunset unlocked</> : "Below threshold"}
        </span>
      </div>
      <div className="mt-2 w-full bg-stone-100 rounded-full h-1.5 overflow-hidden relative">
        <div
          className={above ? "bg-emerald-600 h-full" : "bg-stone-400 h-full"}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
        <div
          className="absolute top-0 w-[2px] h-full bg-red-500 opacity-70"
          style={{ left: `${threshold * 100}%` }}
        />
      </div>
      <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] text-stone-600 font-mono">
        <MiniStat label="Active pool" value={c.active_pool} />
        <MiniStat label="URLs discovered" value={c.admissions_stats_url_discovered} />
        <MiniStat label="Verified in pool" value={c.offer_rate_verified_in_active} />
        <MiniStat
          label={c.sunset_done > 0 ? "Stubs sunset" : "Stubs remaining"}
          value={c.sunset_done > 0 ? c.sunset_done : c.stubs_remaining}
        />
      </div>
      {subjects.length > 0 && (
        <div className="mt-3 pt-3 border-t border-stone-100" data-testid={`router-subjects-${c.country}`}>
          <div className="text-[9px] font-underground-sc tracking-wider text-stone-400 mb-1.5">
            Degrees enriched by subject-router
          </div>
          <div className="flex flex-wrap gap-1.5">
            {subjects.map((s) => {
              const overMapped = s.count > OVER_MAP_THRESHOLD;
              return (
                <span
                  key={s.subject}
                  data-testid={`router-pill-${s.subject}`}
                  title={overMapped
                    ? `⚠ ${s.count} degrees enriched under "${s.subject}" — the subject-router regex might be over-mapping. Review the map in scripts/route_uni_offer_stats_to_courses.py.`
                    : `${s.count} matching degrees received the uni-level offer_rate via the subject-router.`}
                  className={`inline-flex items-center gap-1 rounded-full border text-[10px] font-mono px-2 py-0.5 ${
                    overMapped
                      ? "border-amber-300 bg-amber-50 text-amber-900"
                      : "border-stone-200 bg-stone-50 text-stone-700"
                  }`}
                >
                  {overMapped && <AlertTriangle className="h-2.5 w-2.5" />}
                  {s.subject}
                  <span className="text-stone-400">×</span>
                  <span className="font-semibold">{s.count}</span>
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div>
      <div className="text-stone-400 uppercase tracking-wider text-[9px]">{label}</div>
      <div>{(value ?? 0).toLocaleString()}</div>
    </div>
  );
}


function _relativeAge(iso) {
  if (!iso) return "";
  try {
    const diff = Date.now() - new Date(iso).getTime();
    if (diff < 60_000) return "just now";
    const mins = Math.floor(diff / 60_000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  } catch { return ""; }
}


function _confidenceClass(c) {
  switch (c) {
    case "high":   return "border-emerald-200 bg-emerald-50 text-emerald-800";
    case "medium": return "border-stone-200 bg-stone-50 text-stone-700";
    case "low":    return "border-amber-200 bg-amber-50 text-amber-800";
    default:       return "border-stone-200 bg-stone-50 text-stone-500";
  }
}


function RecentExtractionsFeed({ items }) {
  if (!items || items.length === 0) {
    return (
      <div
        data-testid="recent-extractions-empty"
        className="mt-5 pt-5 border-t border-stone-100"
      >
        <div className="text-[10px] font-underground-sc tracking-widest text-stone-500 mb-2">
          Recent extractions
        </div>
        <div className="text-xs text-stone-400 italic">
          No offer_stats extracted yet — feed will populate as the daemon writes.
        </div>
      </div>
    );
  }
  return (
    <div
      data-testid="recent-extractions-feed"
      className="mt-5 pt-5 border-t border-stone-100"
    >
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-[10px] font-underground-sc tracking-widest text-stone-500">
          Recent extractions · newest first
        </div>
        <div className="text-[10px] font-mono text-stone-400">
          Spot-check accuracy in real time
        </div>
      </div>
      <div className="border border-stone-200 rounded-lg divide-y divide-stone-100 overflow-hidden">
        {items.map((item, i) => (
          <RecentExtractionRow key={`${item.id}-${i}`} item={item} />
        ))}
      </div>
    </div>
  );
}


function RecentExtractionRow({ item }) {
  const pct = typeof item.offer_rate === "number" ? (item.offer_rate * 100).toFixed(1) : "—";
  const age = _relativeAge(item.extracted_at);
  return (
    <div
      data-testid={`recent-extraction-${item.id}`}
      className="grid grid-cols-12 gap-2 items-center px-3 py-2 text-xs hover:bg-stone-50 transition"
    >
      <div className="col-span-4 min-w-0">
        <Link
          to={`/uni/${item.id}`}
          target="_blank"
          className="font-editorial text-sm truncate hover:underline block"
          title={item.name}
        >
          {item.name || item.id}
        </Link>
        <div className="text-[10px] font-underground-sc tracking-wider text-stone-400 truncate">
          {item.country}{item.level ? ` · ${item.level}` : ""}
          {item.cycle_year ? ` · cycle ${item.cycle_year}` : ""}
        </div>
      </div>
      <div className="col-span-2 font-mono text-right">
        <div className="text-base font-semibold text-stone-800" data-testid={`recent-rate-${item.id}`}>
          {pct}%
        </div>
        {item.applications != null && item.offers != null && (
          <div className="text-[9px] text-stone-400">
            {item.offers.toLocaleString()} / {item.applications.toLocaleString()}
          </div>
        )}
      </div>
      <div className="col-span-2 flex justify-center">
        <span
          title={item.quote || ""}
          className={`inline-flex items-center rounded-full border px-1.5 py-[1px] text-[9px] font-underground-sc tracking-wider ${_confidenceClass(item.confidence)}`}
        >
          {item.confidence || "med"}
        </span>
      </div>
      <div className="col-span-2 text-stone-500 text-right font-mono text-[10px]" title={item.extracted_at}>
        {age}
      </div>
      <div className="col-span-2 text-right">
        {item.source_url ? (
          <a
            href={item.source_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-[10px] font-mono text-stone-600 hover:text-stone-900 hover:underline"
            title={item.source_url}
          >
            source <ExternalLink className="h-2.5 w-2.5" />
          </a>
        ) : null}
      </div>
      {item.notes && (
        <div
          data-testid={`recent-notes-${item.id}`}
          className="col-span-12 -mt-1 mb-0.5 text-[10px] text-amber-800 italic bg-amber-50/50 rounded px-2 py-0.5"
        >
          ⚠ {item.notes}
        </div>
      )}
    </div>
  );
}
